import React, { useState, useEffect } from "react";
import { FaUsers, FaTicketAlt, FaCubes, FaUserCircle } from "react-icons/fa";
import { FiSearch, FiPlus } from "react-icons/fi";
import { IoTrendingUp, IoTrendingDown } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { pb, fetchDashboardStats } from '../api/pocketbase';

const BRAND_GREEN = "#90c740";

const stripHtml = (html) => {
  if (!html) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || "";
};

const mapNewsRecord = (record) => ({
  id: record.id,
  image: record.image || "",
  category: record.category || "",
  headline: record.Headline || "",
  date: record.created ? new Date(record.created).toLocaleDateString() : "",
  author: record.expand?.Author
    ? `${record.expand.Author.Name || ""} ${record.expand.Author.Surname || ""}`.trim()
    : "Unknown",
  description: stripHtml(record.News),
});

const Dashboard = () => {
  const navigate = useNavigate();
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [newsForm, setNewsForm] = useState({
    Headline: '',
    News: '',
    Author: '',
    ShowInAAP: true,
    image: '',
    category: '',
    date: '',
    description: '',
  });
  const [news, setNews] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [ticketsOpen, setTicketsOpen] = useState(0);
  const [totalSeats, setTotalSeats] = useState(0);
  const [churnRate, setChurnRate] = useState(0);
  const [customerTrend, setCustomerTrend] = useState("+0");
  const [growthData, setGrowthData] = useState([]);
  const [newThisMonth, setNewThisMonth] = useState(0);

  const handleWidgetClick = (widget) => {
    if (widget === "Customers") navigate("/companies");
    if (widget === "Tickets") navigate("/tickets");
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const [stats, newsRecords] = await Promise.all([
          fetchDashboardStats(),
          pb.collection("News").getFullList({
            sort: "-created",
            expand: "Author",
          }),
        ]);
        setTotalCustomers(stats.totalCustomers);
        setTicketsOpen(stats.openTickets);
        setTotalSeats(stats.totalSeats);
        setChurnRate(stats.churnRate);
        setCustomerTrend(stats.customerTrend);
        setGrowthData(stats.growthData);
        setNewThisMonth(stats.newThisMonth);
        setNews(newsRecords.map(mapNewsRecord));
      } catch (err) {
        console.error("Error loading dashboard data:", err);
      }
    };
    loadDashboardData();
  }, []);

  // Carousel navigation
  const nextNews = () => {
    if (!news.length) return;
    setCarouselIndex((carouselIndex + 1) % news.length);
  };
  const prevNews = () => {
    if (!news.length) return;
    setCarouselIndex((carouselIndex - 1 + news.length) % news.length);
  };

  // Handle news form input
  const handleNewsInput = (e) => {
    const { name, value, type, checked } = e.target;
    setNewsForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Submit news to PocketBase
  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const PB_AUTH = JSON.parse(localStorage.getItem("pocketbase_auth") || "null");
      const authorId = PB_AUTH?.record?.id || "";
      await pb.collection('News').create({
        Headline: newsForm.Headline,
        News: newsForm.News,
        Author: authorId || undefined,
        ShowInAAP: newsForm.ShowInAAP,
      });
      const newsRecords = await pb.collection("News").getFullList({
        sort: "-created",
        expand: "Author",
      });
      setNews(newsRecords.map(mapNewsRecord));
      setCarouselIndex(0);
      setShowNewsModal(false);
      setNewsForm({ Headline: '', News: '', Author: '', ShowInAAP: true, image: '', category: '', date: '', description: '' });
    } catch (err) {
      setError('Failed to add news.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Bar */}
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 md:px-10 py-4 sm:py-6 bg-white shadow-sm rounded-bl-3xl rounded-br-3xl mb-6 sm:mb-8 gap-4 sm:gap-0">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Overview</h1>
        <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <input type="text" placeholder="Search..." className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 outline-none bg-gray-50 focus:ring-[#90c740] text-sm sm:text-base" />
            <FiSearch className="absolute left-3 top-2.5" style={{ color: BRAND_GREEN }} />
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="flex-1 px-2 sm:px-4 md:px-10 pb-6 sm:pb-10 w-full">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Customers Card */}
          <div
            onClick={() => handleWidgetClick("Customers")}
            className="bg-white rounded-xl p-6 cursor-pointer transition-all duration-300 hover:shadow-lg group relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-[#90c740]"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-[#90c740]/10 rounded-lg">
                <FaUsers className="text-[#90c740] text-xl" />
              </div>
              {customerTrend.startsWith("+") ? 
                <div className="flex items-center text-[#90c740] text-sm font-medium">
                  <IoTrendingUp className="mr-1" /> {customerTrend}
                </div> :
                <div className="flex items-center text-red-500 text-sm font-medium">
                  <IoTrendingDown className="mr-1" /> {customerTrend}
                </div>
              }
            </div>
            <h3 className="text-gray-600 font-medium mb-1">Total Customers</h3>
            <div className="text-3xl font-bold text-gray-900">{totalCustomers}</div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-gray-500 text-sm">{newThisMonth} new this month</div>
            </div>
          </div>
          {/* Tickets Card */}
          <div
            onClick={() => handleWidgetClick("Tickets")}
            className="bg-white rounded-xl p-6 cursor-pointer transition-all duration-300 hover:shadow-lg group relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-[#90c740]"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-[#90c740]/10 rounded-lg">
                <FaTicketAlt className="text-[#90c740] text-xl" />
              </div>
            </div>
            <h3 className="text-gray-600 font-medium mb-1">Open Tickets</h3>
            <div className="text-3xl font-bold text-gray-900">{ticketsOpen}</div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-gray-500 text-sm">Parent tickets still open</div>
            </div>
          </div>
          {/* Modules Card */}
          <div
            className="bg-white rounded-xl p-6 transition-all duration-300 hover:shadow-lg group relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-[#90c740]"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-[#90c740]/10 rounded-lg">
                <FaCubes className="text-[#90c740] text-xl" />
              </div>
            </div>
            <h3 className="text-gray-600 font-medium mb-1">Licensed seats</h3>
            <div className="text-3xl font-bold text-gray-900">{totalSeats}</div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-gray-500 text-sm">Seats where genesis is true</div>
            </div>
          </div>
          {/* Visitors Card */}
          <div
            className="bg-white rounded-xl p-6 transition-all duration-300 hover:shadow-lg group relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-[#90c740]"></div>
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-[#90c740]/10 rounded-lg">
                <FaUserCircle className="text-[#90c740] text-xl" />
              </div>
            </div>
            <h3 className="text-gray-600 font-medium mb-1">Inactive customers</h3>
            <div className="text-3xl font-bold text-gray-900">{Math.round((churnRate / 100) * totalCustomers)}</div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-gray-500 text-sm">{churnRate}% of all customers</div>
            </div>
          </div>
        </div>
        {/* Graph + Churn Row */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          {/* Customer Growth Card */}
          <div className="bg-white rounded-xl p-4 sm:p-6 xl:col-span-2">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-gray-900 font-bold text-lg">Customer Growth</h3>
                <p className="text-gray-500 text-sm mt-1">Monthly acquisition trend</p>
              </div>
              <select className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white sm:w-auto">
                <option>Last 6 months</option>
                <option>Last year</option>
              </select>
            </div>
            <div className="h-64 bg-white rounded-lg mb-4 relative overflow-x-auto">
              <div className="absolute inset-0 flex min-w-[420px] items-end justify-around p-4 sm:min-w-0">
                {growthData.map((data, index) => {
                  const maxCustomers = Math.max(...growthData.map((item) => item.customers), 1);
                  const heightPercentage = (data.customers / maxCustomers) * 100;
                  return (
                    <div key={index} className="flex flex-col items-center w-1/6">
                      <div 
                        className="w-8 sm:w-12 bg-[#90c740] hover:bg-[#7ebd2e] transition-all duration-300 rounded-t-md relative group"
                        style={{ height: `${heightPercentage}%`, minHeight: '4px' }}
                      >
                        <div className="hidden group-hover:block absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
                          {data.customers} customers
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 mt-2">{data.month}</span>
                    </div>
                  );
                })}
              </div>
              {/* Y-axis labels */}
              <div className="absolute left-2 inset-y-4 flex flex-col justify-between">
                {(() => {
                  const maxCustomers = Math.max(...growthData.map((item) => item.customers), 4);
                  return [maxCustomers, Math.round(maxCustomers * 0.75), Math.round(maxCustomers * 0.5), Math.round(maxCustomers * 0.25), 0].map((value, index) => (
                    <div key={index} className="text-xs text-gray-400">
                      {value}
                    </div>
                  ));
                })()}
              </div>
              {/* Grid lines */}
              <div className="absolute inset-0 flex flex-col justify-between p-4 pointer-events-none">
                {[0, 1, 2, 3, 4].map((_, index) => (
                  <div key={index} className="w-full h-px bg-gray-100"></div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-900">Total customers</div>
                <div className="text-lg font-bold text-[#90c740] mt-1">
                  {totalCustomers}
                </div>
                <div className="text-xs text-gray-500 mt-1">All records</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-900">New Customers</div>
                <div className="text-lg font-bold text-[#90c740] mt-1">
                  +{newThisMonth}
                </div>
                <div className="text-xs text-gray-500 mt-1">This month</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-900">Inactive share</div>
                <div className="text-lg font-bold text-[#90c740] mt-1">
                  {churnRate}%
                </div>
                <div className="text-xs text-gray-500 mt-1">active = false</div>
              </div>
            </div>
          </div>
          {/* Churn Rate Card */}
          <div className="bg-white rounded-xl p-4 sm:p-6">
            <div className="mb-6">
              <h3 className="text-gray-900 font-bold text-lg">Churn Analysis</h3>
              <p className="text-gray-500 text-sm mt-1">Customer retention overview</p>
            </div>
            <div className="relative pt-2">
              <div className="flex items-center justify-center mb-4">
                <div className="relative w-32 h-32">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-3xl font-bold text-gray-900">{churnRate}%</div>
                  </div>
                  <svg className="transform -rotate-90 w-32 h-32">
                    <circle
                      className="text-gray-200"
                      strokeWidth="8"
                      stroke="currentColor"
                      fill="transparent"
                      r="58"
                      cx="64"
                      cy="64"
                    />
                    <circle
                      className="text-[#90c740]"
                      strokeWidth="8"
                      strokeDasharray={`${churnRate * 3.6} 360`}
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="58"
                      cx="64"
                      cy="64"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-sm">Annual churn rate</p>
                <button className="mt-4 text-[#90c740] hover:text-[#7ebd2e] font-medium text-sm">View Details →</button>
              </div>
            </div>
          </div>
        </div>
        {/* News Carousel Section */}
        <section className="w-full bg-white rounded-xl p-4 sm:p-6 flex flex-col items-center relative">
          <div className="flex flex-col sm:flex-row w-full justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4 sm:gap-0">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Latest News</h2>
              <p className="text-gray-500 text-sm mt-1">Stay updated with company announcements</p>
            </div>
            <button onClick={() => setShowNewsModal(true)} className="flex items-center gap-2 bg-[#90c740] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#7ebd2e] transition-colors duration-200">
              <FiPlus className="text-lg" /> Add News
            </button>
          </div>
          {/* Carousel */}
          <div className="w-full max-w-full sm:max-w-4xl flex items-center justify-center gap-2 sm:gap-8 overflow-hidden">
            <button onClick={prevNews} className="text-2xl sm:text-3xl text-[#90c740] px-2 py-1 rounded-full hover:bg-[#eaf6d5] transition">&#8592;</button>
            <div className="flex-1 flex flex-col items-center min-w-0">
              {news.length > 0 ? (
                <>
                  {news[carouselIndex].image ? (
                    <img src={news[carouselIndex].image} alt={news[carouselIndex].headline} className="w-full max-w-xs sm:max-w-xl h-40 sm:h-64 object-cover rounded-xl shadow mb-2 sm:mb-4" />
                  ) : null}
                  <div className="w-full flex flex-col items-center">
                    {news[carouselIndex].category ? (
                      <span className="text-xs sm:text-sm font-bold uppercase mb-1" style={{ color: BRAND_GREEN }}>{news[carouselIndex].category}</span>
                    ) : null}
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{news[carouselIndex].headline}</h3>
                    <div className="text-gray-500 text-xs mb-1">By {news[carouselIndex].author} • {news[carouselIndex].date}</div>
                    <div className="text-gray-600 text-xs sm:text-base text-center max-w-xs sm:max-w-2xl">{news[carouselIndex].description}</div>
                  </div>
                </>
              ) : (
                <div className="text-gray-500 text-sm py-8">No news yet.</div>
              )}
            </div>
            <button onClick={nextNews} className="text-2xl sm:text-3xl text-[#90c740] px-2 py-1 rounded-full hover:bg-[#eaf6d5] transition">&#8594;</button>
          </div>
        </section>
        {/* News Modal */}
        {showNewsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-2 py-4">
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
              <button onClick={() => setShowNewsModal(false)} className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-700 text-2xl">&times;</button>
              <h2 className="text-lg sm:text-xl font-bold mb-2 sm:mb-4 text-gray-900">Add News</h2>
              <form onSubmit={handleNewsSubmit} className="flex flex-col gap-2 sm:gap-4">
                <input name="Headline" value={newsForm.Headline} onChange={handleNewsInput} placeholder="Headline" className="border rounded-lg p-2 sm:p-3 text-sm sm:text-base" required />
                <textarea name="News" value={newsForm.News} onChange={handleNewsInput} placeholder="News Content" className="border rounded-lg p-2 sm:p-3 text-sm sm:text-base" required />
                <label className="flex items-center gap-2 text-xs sm:text-base">
                  <input type="checkbox" name="ShowInAAP" checked={newsForm.ShowInAAP} onChange={handleNewsInput} /> Show in AAP
                </label>
                <button type="submit" className="bg-[#90c740] text-white font-bold px-4 sm:px-6 py-2 rounded-lg shadow hover:bg-[#7ebd2e] transition text-sm sm:text-base" disabled={loading}>{loading ? 'Adding...' : 'Add News'}</button>
                {error && <div className="text-red-500 text-xs sm:text-sm">{error}</div>}
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
